import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Alert,
  Autocomplete,
} from "@mui/material";
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { apiService } from "../services/api";
import { DashboardLayout } from "../app/layout/DashboardLayout";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Product, Customer } from "../types";

interface OrderItem {
  product_name: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  product_id?: string;
}

interface CreateOrderForm {
  customer_name: string;
  customer_phone: string;
  customer_address: string;
  customer_email: string;
  items: OrderItem[];
  notes: string;
}

const CreateOrder: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [formData, setFormData] = useState<CreateOrderForm>({
    customer_name: "",
    customer_phone: "",
    customer_address: "",
    customer_email: "",
    items: [],
    notes: "",
  });

  const [newItem, setNewItem] = useState({
    product_name: "",
    quantity: 1,
    unit_price: 0,
    product_id: "",
  });

  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(
    null
  );

  // Fetch customers with React Query
  const { data: customersData, isLoading: customersLoading } = useQuery({
    queryKey: ["customers", "create-order"],
    queryFn: async () => {
      const response = await apiService.get<{
        customers: Customer[];
      }>("/customers");
      console.log("Customers API response:", response);
      return response.data;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
  });

  const customers = customersData?.customers || [];
  console.log("Customers array:", customers);

  // Fetch products with React Query
  const { data: productsData, isLoading: productsLoading } = useQuery({
    queryKey: ["products", "create-order"],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append("status", "active");
      params.append("limit", "50");

      const response = await apiService.get<{
        data: Product[];
        pagination: any;
      }>(`/products?${params.toString()}`);
      console.log("Products API response:", response);
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const products = productsData?.data || [];
  console.log("Products array:", products);

  // Create order mutation
  const createOrderMutation = useMutation({
    mutationFn: async (orderData: CreateOrderForm) => {
      const response = await apiService.post("/orders", orderData);
      return response;
    },
    onSuccess: () => {
      // Invalidate all related queries to refresh data
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-stats"] });
      queryClient.invalidateQueries({ queryKey: ["recent-orders"] });
      queryClient.invalidateQueries({ queryKey: ["my-orders"] });
      setSuccess("Sipariş başarıyla oluşturuldu!");
      setTimeout(() => {
        navigate("/orders");
      }, 2000);
    },
    onError: (error: any) => {
      setError(error.message || "Sipariş oluşturulurken hata oluştu");
    },
  });

  const handleInputChange = (field: keyof CreateOrderForm, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleNewItemChange = (field: string, value: any) => {
    setNewItem((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCustomerSelect = (customer: Customer | null) => {
    setSelectedCustomer(customer);
    if (customer) {
      setFormData((prev) => ({
        ...prev,
        customer_name: customer.full_name,
        customer_phone: customer.phone || "",
        customer_address: customer.address,
        customer_email: customer.email || "",
      }));
    }
  };

  const handleProductSelect = (product: Product | null) => {
    if (product) {
      setNewItem({
        product_name: product.name,
        quantity: 1,
        unit_price: product.selling_price,
        product_id: product._id,
      });
    }
  };

  const addItem = () => {
    if (
      !newItem.product_name ||
      newItem.quantity <= 0 ||
      newItem.unit_price <= 0
    ) {
      setError("Lütfen tüm alanları doldurun");
      return;
    }

    const total_price = newItem.quantity * newItem.unit_price;
    const item: OrderItem = {
      product_name: newItem.product_name,
      quantity: newItem.quantity,
      unit_price: newItem.unit_price,
      total_price,
      product_id: newItem.product_id,
    };

    setFormData((prev) => ({
      ...prev,
      items: [...prev.items, item],
    }));

    // Reset new item form
    setNewItem({
      product_name: "",
      quantity: 1,
      unit_price: 0,
      product_id: "",
    });
  };

  const removeItem = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const calculateTotal = () => {
    return formData.items.reduce((total, item) => total + item.total_price, 0);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.customer_name || formData.items.length === 0) {
      setError("Lütfen müşteri bilgilerini ve en az bir ürün ekleyin");
      return;
    }

    setLoading(true);
    setError("");

    try {
      await createOrderMutation.mutateAsync(formData);
    } catch (error) {
      console.error("Error creating order:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("tr-TR", {
      style: "currency",
      currency: "TRY",
    }).format(amount);
  };

  return (
    <DashboardLayout title="Yeni Sipariş Oluştur">
      <Container maxWidth="lg">
        {/* Success/Error Alerts */}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Paper sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom>
            Yeni Sipariş Oluştur
          </Typography>

          <form onSubmit={handleSubmit}>
            {/* Customer Information */}
            <Typography variant="h6" gutterBottom>
              Müşteri Bilgileri
            </Typography>
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12}>
                <Autocomplete
                  options={customers}
                  getOptionLabel={(option) =>
                    `${option.full_name} ${option.company_name ? `(${option.company_name})` : ""}`
                  }
                  value={selectedCustomer}
                  onChange={(_, value) => handleCustomerSelect(value)}
                  loading={customersLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Müşteri Seç"
                      placeholder="Müşteri arayın..."
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Box>
                        <Typography variant="body1">
                          {option.full_name}
                        </Typography>
                        {option.company_name && (
                          <Typography variant="body2" color="text.secondary">
                            {option.company_name}
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary">
                          {option.phone} • {option.email}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Müşteri Adı"
                  value={formData.customer_name}
                  onChange={(e) =>
                    handleInputChange("customer_name", e.target.value)
                  }
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Müşteri Telefonu"
                  value={formData.customer_phone}
                  onChange={(e) =>
                    handleInputChange("customer_phone", e.target.value)
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Müşteri Adresi"
                  value={formData.customer_address}
                  onChange={(e) =>
                    handleInputChange("customer_address", e.target.value)
                  }
                  multiline
                  rows={2}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Müşteri E-posta"
                  type="email"
                  value={formData.customer_email}
                  onChange={(e) =>
                    handleInputChange("customer_email", e.target.value)
                  }
                />
              </Grid>
            </Grid>

            {/* Product Selection */}
            <Typography variant="h6" gutterBottom>
              Ürün Ekle
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={products}
                  getOptionLabel={(option) =>
                    `${option.name} (${option.product_code})`
                  }
                  value={
                    products.find((p) => p._id === newItem.product_id) || null
                  }
                  onChange={(_, value) => handleProductSelect(value)}
                  loading={productsLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Ürün Seç"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Box>
                        <Typography variant="body1">{option.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.product_code} •{" "}
                          {formatCurrency(option.selling_price)} • Stok:{" "}
                          {option.current_stock}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Miktar"
                  type="number"
                  value={newItem.quantity}
                  onChange={(e) =>
                    handleNewItemChange(
                      "quantity",
                      parseInt(e.target.value) || 0
                    )
                  }
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Birim Fiyat"
                  type="number"
                  value={newItem.unit_price}
                  onChange={(e) =>
                    handleNewItemChange(
                      "unit_price",
                      parseFloat(e.target.value) || 0
                    )
                  }
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={addItem}
                  disabled={
                    !newItem.product_name ||
                    newItem.quantity <= 0 ||
                    newItem.unit_price <= 0
                  }
                >
                  Ürün Ekle
                </Button>
              </Grid>
            </Grid>

            {/* Order Items List */}
            {formData.items.length > 0 && (
              <>
                <Typography variant="h6" gutterBottom>
                  Sipariş Kalemleri
                </Typography>
                <List>
                  {formData.items.map((item, index) => (
                    <React.Fragment key={index}>
                      <ListItem>
                        <ListItemText
                          primary={item.product_name}
                          secondary={`${item.quantity} adet x ${formatCurrency(item.unit_price)}`}
                        />
                        <ListItemSecondaryAction>
                          <Box display="flex" alignItems="center" gap={2}>
                            <Typography variant="h6">
                              {formatCurrency(item.total_price)}
                            </Typography>
                            <IconButton
                              color="error"
                              onClick={() => removeItem(index)}
                            >
                              <RemoveIcon />
                            </IconButton>
                          </Box>
                        </ListItemSecondaryAction>
                      </ListItem>
                      <Divider />
                    </React.Fragment>
                  ))}
                </List>
                <Box display="flex" justifyContent="flex-end" mt={2}>
                  <Typography variant="h5" color="primary">
                    Toplam: {formatCurrency(calculateTotal())}
                  </Typography>
                </Box>
              </>
            )}

            {/* Notes */}
            <Grid container spacing={3} sx={{ mt: 3 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notlar"
                  value={formData.notes}
                  onChange={(e) => handleInputChange("notes", e.target.value)}
                  multiline
                  rows={3}
                />
              </Grid>
            </Grid>

            {/* Submit Buttons */}
            <Box display="flex" gap={2} mt={3}>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={loading || formData.items.length === 0}
              >
                {loading ? "Oluşturuluyor..." : "Sipariş Oluştur"}
              </Button>
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={() => navigate("/orders")}
              >
                İptal
              </Button>
            </Box>
          </form>
        </Paper>
      </Container>
    </DashboardLayout>
  );
};

export default CreateOrder;
